import {SVG} from '../../../../../constants/assetLists';
import {useMemo} from 'react';
import {ColumnDef} from '../../../../../components/common/AppTable';
import AppGroupedTable, {
  GroupNode,
} from '../../../../../components/common/AppGroupedTable';
import {useRolesContext} from '../../../../../context/users/RolesContextProvider';
import {ICONS} from '../../../data';
import {RoleItem, RoleGroupClient} from '../roles';
import {getRoleTypeName} from '../../../../../utils';

function toGroupNodes(groups: RoleGroupClient[]): GroupNode<RoleItem>[] {
  return groups.map(client => ({
    id: client.clientId,
    label: client.clientName,
    own: client.own,
    children: client.branches.map(branch => ({
      id: branch.branchId,
      label: branch.branchName,
      own: branch.own,
      children: branch.subBranches.map(sub => ({
        id: sub.subBranchId,
        label: sub.subBranchName,
        own: sub.own,
      })),
    })),
  }));
}

function RolesTable() {
  const {groupedRoles, selectedRoles, setSelectedRoles, searchText} =
    useRolesContext();

  const columns = useMemo<ColumnDef<RoleItem>[]>(
    () => [
      {
        title: 'תפקיד',
        field: 'name',
        accessor: r => getRoleTypeName(r?.roleType) || '-',
        searchable: true,
        render: r => {
          const roleIcon = ICONS?.find(icon => icon.id === r?.icon)?.icon || '';
          return (
            <div className="flex items-center gap-2">
              {roleIcon && (
                <img
                  src={roleIcon}
                  alt="icon"
                  className="w-[20px] h-[20px] object-contain"
                />
              )}
              <span>{getRoleTypeName(r?.roleType) || '-'}</span>
            </div>
          );
        },
      },
      {
        title: 'תיאור',
        field: 'description',
        accessor: 'description',
        width: 400,
        searchable: true,
        render: r => (
          <div className="whitespace-normal break-words">
            {r?.description || '-'}
          </div>
        ),
      },
      {
        title: 'צבע',
        field: 'color',
        accessor: 'color',
        render: r => (
          <div
            className="w-[16px] h-[16px] rounded-[3px] border-1 border-[#707070]"
            style={{backgroundColor: r?.color}}
          />
        ),
      },
      {
        title: 'סטאטוס תפקיד',
        field: 'status',
        accessor: 'status',
        render: r => (
          <div className="flex items-center gap-2">
            {r.status === 'active' ? (
              <div className="w-[8px] h-[8px] rounded-full bg-green" />
            ) : (
              <img
                src={SVG.closeGrayIcon}
                alt="icon"
                className="w-[10px] h-[10px] object-contain"
              />
            )}
            <span>{r?.status === 'active' ? 'פעיל' : 'לא פעיל'}</span>
          </div>
        ),
      },
      {
        title: 'בית עסק',
        field: 'business',
        accessor: r => r?.branchIds?.map((b: any) => b.name)?.join(', ') || '-',
      },
      {
        title: 'משויך למחלקה/ות',
        field: 'associatedGroups',
        accessor: r => r?.departmentId?.map((g: any) => g.name)?.join(', '),
      },
    ],
    [],
  );

  const groups = useMemo(
    () => toGroupNodes(groupedRoles?.groups || []),
    [groupedRoles],
  );

  return (
    <AppGroupedTable<RoleItem>
      groups={groups}
      columns={columns}
      rowKey={r => r?._id}
      selectable
      selectedKeys={selectedRoles}
      onSelectionChange={setSelectedRoles}
      rowDisabled={r => r.status !== 'active'}
      searchText={searchText}
    />
  );
}

export default RolesTable;
